import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowLeftIcon, ImageIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { MAX_IMAGE_MB, validateImage } from '../../services/imageService';
import { caseCategories, caseStatuses, priceModels } from '../../types/admin';
import type { CaseCategory, CaseStatus, PriceModel } from '../../types/admin';
const NEW_CUSTOMER = 'ny';
interface Selected {
  file: File;
  preview: string;
}
export function CaseNew() {
  const {
    customers,
    createCase,
    settings
  } = useAdminData();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? NEW_CUSTOMER);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CaseCategory>('Multiservice');
  const [priceModel, setPriceModel] = useState<PriceModel>('Tilbud endnu ikke lavet');
  const [hourlyRate, setHourlyRate] = useState(settings.hourlyRate);
  const [fixedPrice, setFixedPrice] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [status, setStatus] = useState<CaseStatus>('Ny henvendelse');
  const [internalNote, setInternalNote] = useState('');
  const [photos, setPhotos] = useState<Selected[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const creatingCustomer = customerId === NEW_CUSTOMER || customers.length === 0;
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1015" data-mp="8djfa7pajwcv">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-1016" data-mp="dbbi3wrh52vu">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1017" data-mp="2f2k1d1rhtny6" />
        Sager
      </Link>
      <h1 className="mt-4 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1018" data-mp="2a8fsue2kx7uz">
        Opret sag
      </h1>

      <form className="mt-7 max-w-3xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      setError('');
      const created = await createCase({
        customerId: creatingCustomer ? '' : customerId,
        newCustomer: creatingCustomer ? {
          name,
          phone,
          email,
          address
        } : undefined,
        title,
        address,
        description,
        category,
        priceModel,
        hourlyRate: priceModel === 'Timepris' ? hourlyRate : '',
        fixedPrice: priceModel === 'Fast pris' ? fixedPrice : '',
        plannedDate,
        status,
        internalNote,
        photos: photos.map(photo => ({
          url: photo.preview,
          name: photo.file.name,
          file: photo.file
        }))
      });
      setSaving(false);
      if (!created) {
        setError('Sagen kunne ikke gemmes. Tjek din forbindelse, og prøv igen.');
        return;
      }
      navigate(`/admin/sager/${created.id}`);
    }} data-id="element-1019" data-mp="219m04ysdd1mp">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-1020" data-mp="6sucd6f0iygh">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1021" data-mp="6uycredpdfc0">Kunde</h2>
          <SelectField id="ns-kunde" label="Vælg kunde" className="mt-5" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[...customers.map(customer => ({
          value: customer.id,
          label: customer.name
        })), {
          value: NEW_CUSTOMER,
          label: '+ Opret ny kunde'
        }]} data-id="element-1022" data-mp="bq40miqo142q" />
          {creatingCustomer && <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1023" data-mp="1r91xskwegawk">
              <TextField id="ns-navn" label="Navn" required value={name} onChange={event => setName(event.target.value)} data-id="element-1024" data-mp="1rbo46h7k0kd7" />
              <TextField id="ns-tlf" label="Telefonnummer" type="tel" value={phone} onChange={event => setPhone(event.target.value)} data-id="element-1025" data-mp="1w9645i3lfp92" />
              <TextField id="ns-mail" label="E-mail" type="email" className="sm:col-span-2" value={email} onChange={event => setEmail(event.target.value)} data-id="element-1026" data-mp="13la8mgprg3dv" />
            </div>}
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1027" data-mp="pr9j8dkp7sq6">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1028" data-mp="kuj1llt8wp2n">Opgave</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1029" data-mp="2wguq7r7rdu6a">
            <TextField id="ns-titel" label="Opgavetitel" required placeholder="Fx montering af hegn" value={title} onChange={event => setTitle(event.target.value)} className="sm:col-span-2" data-id="element-1030" data-mp="2wf9wg3t9qf9h" />
            <TextField id="ns-adresse" label="Adresse" placeholder="Vej, nummer, postnr. og by" value={address} onChange={event => setAddress(event.target.value)} className="sm:col-span-2" data-id="element-1031" data-mp="2rfnghmm22kwz" />
            <SelectField id="ns-kategori" label="Kategori" value={category} onChange={event => setCategory(event.target.value as CaseCategory)} options={caseCategories.map(option => ({
            value: option,
            label: option
          }))} data-id="element-1032" data-mp="3690gmwid43jd" />
            <TextField id="ns-dato" label="Planlagt dato" type="date" value={plannedDate} onChange={event => setPlannedDate(event.target.value)} data-id="element-1033" data-mp="1zohfl3yigj6n" />
          </div>
          <TextArea id="ns-beskrivelse" label="Opgavebeskrivelse" className="mt-5" rows={4} value={description} onChange={event => setDescription(event.target.value)} data-id="element-1034" data-mp="3rp526r2f29sp" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1035" data-mp="3dqouawstid8z">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1036" data-mp="3dsrvmczf7st0">Pris og status</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1037" data-mp="38t5i02fokajo">
            <SelectField id="ns-prisform" label="Prisform" value={priceModel} onChange={event => setPriceModel(event.target.value as PriceModel)} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-1038" data-mp="34r24775jgtv8" />
            <SelectField id="ns-status" label="Status" value={status} onChange={event => setStatus(event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
            value: option,
            label: option
          }))} data-id="element-1039" data-mp="2kvhc3ltrq60a" />
            {priceModel === 'Timepris' && <TextField id="ns-timepris" label="Timepris (kr.)" inputMode="numeric" value={hourlyRate} onChange={event => setHourlyRate(event.target.value)} data-id="element-1040" data-mp="21x4q2zhrd1na" />}
            {priceModel === 'Fast pris' && <TextField id="ns-fastpris" label="Fast pris (kr.)" inputMode="numeric" value={fixedPrice} onChange={event => setFixedPrice(event.target.value)} data-id="element-1041" data-mp="2fwnubh7g7sub" />}
          </div>
          <TextArea id="ns-noter" label="Interne noter" className="mt-5" rows={3} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." value={internalNote} onChange={event => setInternalNote(event.target.value)} data-id="element-1042" data-mp="32zippw2us9lo" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1043" data-mp="3701j3uke9npz">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1044" data-mp="3lph7x50d7j31">Billeder</h2>
          <label htmlFor="ns-billeder" className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-1045" data-mp="3gr5w4oqieyi6">
            <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-1046" data-mp="20ce1s69szh68" />
            <span className="text-[15px] font-medium text-ink" data-id="element-1047" data-mp="1wbc43tvjbd6d">Vælg billeder</span>
            <span className="text-sm text-ink-muted" data-id="element-1048" data-mp="1wdezktfghyih">Op til {MAX_IMAGE_MB} MB pr. billede</span>
            <input id="ns-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
            const input = event.target;
            const accepted: Selected[] = [];
            const problems: string[] = [];
            Array.from(input.files ?? []).forEach(file => {
              const problem = validateImage(file);
              if (problem) problems.push(problem);else accepted.push({
                file,
                preview: URL.createObjectURL(file)
              });
            });
            setPhotos(current => [...current, ...accepted]);
            setError(problems.join(' '));
            input.value = '';
          }} data-id="element-1049" data-mp="1m9hvt04y4bks" />
          </label>
          {photos.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-id="element-1050" data-mp="2svu3i7zeug5i">
              {photos.map((photo, index) => <li key={`${photo.file.name}-${index}`} className="relative" data-id="element-1051" data-mp="duf4z5zlpnoo" data-mpi={index}>
                  <img src={photo.preview} alt={photo.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-1052" data-mp="inqexfzpmkkt" />
                  <button type="button" onClick={() => setPhotos(current => {
              URL.revokeObjectURL(current[index].preview);
              return current.filter((_, i) => i !== index);
            })} aria-label={`Fjern billede ${photo.file.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-1053" data-mp="8xx44h7e69hm">
                    <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-1054" data-mp="3dcoia6ciessh" />
                  </button>
                </li>)}
            </ul>}
        </section>

        {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1055" data-mp="30ry80hlir7mu">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1056" data-mp="3eqxyzvwi7ym6" />
            {error}
          </p>}

        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-1057" data-mp="39r36jnx2z0z0">
          <Button type="submit" size="lg" disabled={saving} data-id="element-1058" data-mp="2m4mzypojq5rh">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1059" data-mp="2h5tk4qlse2o5" />
                Gemmer …
              </> : 'Gem sag'}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/admin/sager')} data-id="element-1060" data-mp="2r1n2iqm6sqdu">
            Annuller
          </Button>
        </div>
      </form>
    </div>;
}